import { ArrowRight } from "@phosphor-icons/react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Avatar, Timeline, Tooltip } from "flowbite-react";
import { TFunction } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import UserActivitySkeleton from "../../../components/SkeletonLoaders/UserActivitySkeleton";
import {
  ACTION_TYPE_META,
  BIDDING_STRATEGY_LABEL_MAPPER,
  DEFAULT_AVATAR,
  DEFAULT_AVATAR_FOR_DELETED_USER,
} from "../../../shared/constants";
import { IUserActivity } from "../interface";
import AccountInfo from "./AccountInfo";
import ActivityChanges from "./ActivityChanges";
import BillingMeta, { isBillingActivity } from "./BillingMeta";

const PILL_DARK: Record<string, string> = {
  green: "dark:bg-green-900/30 dark:text-green-300 dark:border-green-800/50",
  red: "dark:bg-red-900/30 dark:text-red-300 dark:border-red-800/50",
  blue: "dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800/50",
  indigo: "dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800/50",
  yellow: "dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-800/50",
  gray: "dark:bg-neutral-700 dark:text-gray-300 dark:border-neutral-600",
};

dayjs.extend(relativeTime);

const renderChangePill = (
  t: TFunction,
  value: unknown,
  type: "old" | "new",
  field?: string
): JSX.Element => {
  // Special case for profile picture
  if (
    field === "profile_picture" &&
    typeof value === "string" &&
    value.startsWith("http")
  ) {
    return <Avatar img={value} size="sm" rounded alt="profile" />;
  }

  const isBoolean = typeof value === "boolean";
  const stringValue = isBoolean
    ? value
      ? t("common.enabled")
      : t("common.disabled")
    : String(value || "None").trim();

  const normalizedValue = stringValue.toLowerCase();

  const getPill = (text: string, className: string) => (
    <span className={`px-2 py-0.5 rounded border ${className}`}>{text}</span>
  );

  // Determine styles based on type and value
  if (type === "old" && !isBoolean) {
    return getPill(stringValue, "bg-gray-100 dark:bg-neutral-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-neutral-600");
  }

  if (
    normalizedValue === "enabled" ||
    normalizedValue === t("common.enabled").toLowerCase()
  ) {
    return getPill(stringValue, "bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800/50");
  }

  if (
    normalizedValue === "disabled" ||
    normalizedValue === t("common.disabled").toLowerCase()
  ) {
    return getPill(stringValue, "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800/50");
  }

  if (["none", "null", "undefined", ""].includes(normalizedValue)) {
    return getPill("None", "bg-gray-100 dark:bg-neutral-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-neutral-600");
  }

  return getPill(stringValue, "bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800/50");
};

const formatActivityMessage = (
  activity: IUserActivity,
  t: TFunction
): string => {
  const actor = activity.current_user?.name || activity.current_user?.email;
  switch (activity.action_type) {
    case "USER_ROLE_CHANGE":
      return t("userActivity.roleChanged", {
        actor,
        target: activity.meta?.new_changes?.email,
        oldRole: activity.changes?.[0]?.old || "",
        newRole: activity.changes?.[0]?.new || "",
      });
    case "PROFILE_UPDATE":
      return t("userActivity.profileUpdated", { actor });
    case "USER_DELETE":
      return t("userActivity.userDeleted", {
        actor,
        target: activity.meta?.old_changes?.name,
      });
    case "LOGIN":
      return t("userActivity.userLoggedIn", { actor });
    case "LOGOUT":
      return t("userActivity.userLoggedOut", { actor });
    case "INVITE_USER_ACCEPT":
      return t("userActivity.inviteAccepted", { actor });
    case "INVITE_USER_DELETE":
      return t("userActivity.inviteDeleted", {
        actor,
        target: activity.meta?.old_changes?.email,
      });
    case "ROLE_DELETE":
      return t("userActivity.roleDeleted", {
        actor,
        target: activity.meta?.role_name,
      });
    case "ROLE_UPDATE":
      return t("userActivity.roleUpdated", {
        actor,
      });
    case "ROLE_CREATE":
      return t("userActivity.roleCreated", {
        actor,
        target: activity.meta?.role_name,
      });
    case "SIGN_UP":
      return t("userActivity.signUp", { actor });
    case "INVITE_USER_EMAIL_SENT":
      return t("userActivity.inviteEmailSent", {
        actor,
        target: activity.meta?.new_changes?.email,
      });
    case "RESEND_INVITE":
      return t("userActivity.resendInvite", {
        actor,
        target: activity.meta?.email,
      });
    case "PASSWORD_RESET_REQUEST":
      return t("userActivity.passwordResetRequested", { actor });
    case "PASSWORD_RESET_COMPLETE":
      return t("userActivity.passwordResetComplete", { actor });
    case "PASSWORD_CHANGE":
      return t("userActivity.passwordChanged", { actor });
    case "DASHBOARD_PREFERENCE_UPDATE":
      return t("userActivity.dashboardPreferenceUpdated", { actor });
    case "APP_LIST_PREFERENCE_UPDATE":
      return t("userActivity.appListPreferenceUpdated", { actor });
    case "APP_DETAIL_PREFERENCE_UPDATE":
      return t("userActivity.appDetailPreferenceUpdated", { actor });
    case "APP_COUNTRY_REPORT_PREFERENCE_UPDATE":
      return t("userActivity.appCountryReportPreferenceUpdated", { actor });
    case "BID_CHANGE":
      return t("userActivity.bidChanged", {
        actor,
        target: `${activity.meta?.campaign_name} ( ${BIDDING_STRATEGY_LABEL_MAPPER[activity.meta?.bid_method as string]
          } )`,
      });
    case "INTEGRATION_START":
      return t("userActivity.integrationStarted", { actor });
    case "INTEGRATION_COMPLETE":
      return t("userActivity.integrationComplete", { actor });
    case "INTEGRATION_DELETE":
      return t("userActivity.integrationDeleted", { actor });
    case "USER_DEACTIVATED":
      return t("userActivity.userDeactivated");
    case "USER_ACTIVATED":
      return t("userActivity.userActivated");
    case "ADDON_ACCOUNT_DELETE":
      return t("userActivity.accountDeleted", { actor });
    case "ONGOING_ACCOUNT_DELETE":
      return t("userActivity.ongoingAccountDelete", { actor });
    case "ADDON_ACCOUNT_INTEGRATION_CANCEL":
      return t("userActivity.addonAccountIntegrationCancel", { actor });
    case "ADDON_ACCOUNT_INTEGRATION_COMPLETE":
      return t("userActivity.addonAccountIntegrationComplete", { actor });
    case "ADDON_ACCOUNT_INTEGRATION_START":
      return t("userActivity.addonAccountIntegrationStart", { actor, target: String(activity?.meta?.authorise_type).replace("_", " ") });
    case "ENTIRE_TRACKING_DELETE":
      return t("userActivity.entireTrackingDelete", { actor });
    case "UserActivityEventEnum.META_ADS_CONNECT":
      return t("userActivity.metaAdsConnect", { actor });
    case "UserActivityEventEnum.META_ADS_DISCONNECT":
      return t("userActivity.metaAdsDisconnect", { actor });
    case "UserActivityEventEnum.META_ADS_ACCOUNT_SAVE":
      return t("userActivity.metaAdsAccountSave", { actor });
    case "TRIAL_STARTED":
      return t("userActivity.trialStarted", { actor, plan: activity.meta?.plan ?? "" });
    case "TRIAL_ENDED":
      return t("userActivity.trialEnded", { plan: activity.meta?.plan ?? "" });
    case "SUBSCRIPTION_CREATED":
      return t("userActivity.subscriptionCreated", { actor, plan: activity.meta?.plan ?? "" });
    case "SUBSCRIPTION_RENEWED":
      return t("userActivity.subscriptionRenewed", { plan: activity.meta?.plan ?? "" });
    case "SUBSCRIPTION_UPGRADED":
      return t("userActivity.subscriptionUpgraded", { actor });
    case "SUBSCRIPTION_DOWNGRADED":
      return t("userActivity.subscriptionDowngraded", { actor });
    case "SUBSCRIPTION_CANCELLED":
      return t("userActivity.subscriptionCancelled", { actor, plan: activity.meta?.plan ?? "" });
    case "SUBSCRIPTION_REACTIVATED":
      return t("userActivity.subscriptionReactivated", { plan: activity.meta?.plan ?? "" });
    case "PAYMENT_SUCCEEDED":
      return t("userActivity.paymentSucceeded", { plan: activity.meta?.plan ?? "" });
    case "PAYMENT_FAILED":
      return t("userActivity.paymentFailed", { plan: activity.meta?.plan ?? "" });
    case "REFUND_REQUESTED":
      return t("userActivity.refundRequested", { actor, plan: activity.meta?.plan ?? "" });
    case "REFUND_PROCESSED":
      return t("userActivity.refundProcessed", { plan: activity.meta?.plan ?? "" });
    case "GOOGLE_ADS_BID_CHANGE":
      return t("userActivity.googleAdsBidChanged", { actor, target: activity.meta?.campaign_name ?? "" });
    case "GOOGLE_ADS_BUDGET_CHANGE":
      return t("userActivity.googleAdsBudgetChanged", { actor, target: activity.meta?.campaign_name ?? "" });
    case "GOOGLE_ADS_STATUS_CHANGE":
      return t("userActivity.googleAdsStatusChanged", { actor, target: activity.meta?.campaign_name ?? "" });
    case "APPLE_ADS_BID_CHANGE":
      return t("userActivity.appleAdsBidChanged", { actor, target: activity.meta?.campaign_name ?? "" });
    case "APPLE_ADS_BUDGET_CHANGE":
      return t("userActivity.appleAdsBudgetChanged", { actor, target: activity.meta?.campaign_name ?? "" });
    case "APPLE_ADS_STATUS_CHANGE":
      return t("userActivity.appleAdsStatusChanged", { actor, target: activity.meta?.campaign_name ?? "" });
    case "META_ADS_BID_CHANGE":
      return t("userActivity.metaAdsBidChanged", { actor, target: activity.meta?.campaign_name ?? "" });
    case "META_ADS_BUDGET_CHANGE":
      return t("userActivity.metaAdsBudgetChanged", { actor, target: activity.meta?.campaign_name ?? "" });
    case "META_ADS_STATUS_CHANGE":
      return t("userActivity.metaAdsStatusChanged", { actor, target: activity.meta?.campaign_name ?? "" });
    default:
      return activity.action;
  }
};

const Activities = ({
  activityData,
  isLoading,
}: {
  activityData: IUserActivity[];
  isLoading: boolean;
}) => {
  const { t } = useTranslation();

  const DELETED_USER = useMemo(
    () => ({
      id: -1,
      username: t("common.deletedUser"),
      email: "",
      profile_picture: DEFAULT_AVATAR,
      name: t("common.deletedUser"),
      is_active: false,
      is_owner: false,
    }),
    [t]
  );

  const activities: IUserActivity[] = useMemo(() => {
    const results = activityData;
    return results.map((activity: IUserActivity) => ({
      ...activity,
      time: dayjs(activity.created_at).fromNow(),
    }));
  }, [activityData]);

  return (
    <div className="h-full">
      {isLoading ? <UserActivitySkeleton /> : (activities.length > 0 ? (
        <Timeline>
          {activities.map((activity) => {
            const meta = ACTION_TYPE_META[activity.action_type] || {
              label: activity.action_type,
              color: "gray",
              showDiff: false,
            };

            const user = activity.current_user || {
              ...DELETED_USER,
              name: `${DELETED_USER.name} ${activity.meta?.new_changes?.name
                ? `( ${activity.meta?.new_changes?.name} )`
                : ""
                }`,
              email: activity.meta?.new_changes?.email || "",
              profile_picture: DEFAULT_AVATAR_FOR_DELETED_USER,
            };

            const accountId = activity?.meta?.data?.publisher_id ?? activity?.meta?.data?.ads_parent_id ?? activity?.meta?.data?.customer_id ?? activity?.meta?.data?.sub_customer_id ?? activity?.meta?.data?.bucket_name ?? activity?.meta?.data?.vendor_number;

            return (
              <Timeline.Item key={activity.id} className="mb-7">
                <Timeline.Point />
                <Timeline.Content>
                  <Timeline.Time>
                    {dayjs(activity.created_at).format("DD/MM/YYYY h:mm:A")}
                  </Timeline.Time>
                  <div className="flex items-center gap-2 my-1">
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-semibold bg-${meta.color}-100 text-${meta.color}-800 border border-${meta.color}-200 ${PILL_DARK[meta.color] ?? PILL_DARK.gray}`}
                    >
                      {t(`userActivity.actionType.${activity.action_type}`, activity.action_type)}
                    </span>
                    {user.email ? (
                      <Tooltip content={user.email}>
                        <div className="flex items-center gap-1 cursor-help">
                          <Avatar
                            img={user.profile_picture}
                            rounded
                            size="xs"
                            alt={user.name || user.email}
                          />
                          <span className="text-xs text-gray-700 dark:text-gray-300 font-medium ">
                            {user.name || user.email}
                          </span>
                        </div>
                      </Tooltip>
                    ) : (
                      <div className="flex items-center gap-1 cursor-default">
                        <Avatar
                          img={user.profile_picture}
                          rounded
                          size="xs"
                          alt={user.name || user.email}
                        />
                        <span className="text-xs text-gray-700 font-medium">
                          {user.name || user.email}
                        </span>
                      </div>
                    )}
                    {activity.target_user && (
                      <>
                        <ArrowRight
                          size={16}
                          className="text-gray-500 dark:text-gray-400"
                          weight="duotone"
                        />
                        {activity.target_user.email ? (
                          <Tooltip content={activity.target_user.email}>
                            <div className="flex items-center gap-1 cursor-help">
                              <Avatar
                                img={activity.target_user.profile_picture}
                                rounded
                                size="xs"
                                alt={
                                  activity.target_user.name ||
                                  activity.target_user.email
                                }
                              />
                              <span className="text-xs text-gray-700 dark:text-gray-300 font-medium">
                                {activity.target_user.name ||
                                  activity.target_user.email}
                              </span>
                            </div>
                          </Tooltip>
                        ) : (
                          <div className="flex items-center gap-1 cursor-default">
                            <Avatar
                              img={activity.target_user.profile_picture}
                              rounded
                              size="xs"
                              alt={
                                activity.target_user.name ||
                                activity.target_user.email
                              }
                            />
                            <span className="text-xs text-gray-700 dark:text-gray-300 font-medium">
                              {activity.target_user.name ||
                                activity.target_user.email}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <Timeline.Body>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                      {formatActivityMessage(
                        {
                          ...activity,
                          current_user: user,
                        },
                        t
                      )}
                    </p>
                    {meta.showDiff &&
                      activity.changes &&
                      activity.changes.length > 0 && (
                        <ActivityChanges
                          t={t}
                          changes={activity.changes}
                          meta={activity.meta}
                          renderChangePill={renderChangePill}
                        />
                      )}
                    {activity.meta && isBillingActivity(activity.action_type) && (
                      <BillingMeta meta={activity.meta} t={t} />
                    )}
                    {(activity?.meta?.data && activity?.meta?.data?.email) && (
                      <AccountInfo
                        authorizeType={String(
                          activity?.meta?.data?.authorize_type
                        )}
                        email={String(activity?.meta?.data?.email)}
                        accountId={accountId}
                      />
                    )}
                  </Timeline.Body>
                </Timeline.Content>
              </Timeline.Item>
            );
          })}
        </Timeline>
      ) : (
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-500 dark:text-gray-400">{t("userActivity.noActivity")}</p>
        </div>
      ))}
    </div>
  );
};

export default Activities;
